import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Download, RefreshCw, Mail, Phone as PhoneIcon, FileText, Clock, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { BACKEND_URL, adminGet, adminPut, adminDelete, reportError, formatDate, fadeUp } from './adminApi';

export const CAND_STATUS = {
  nouveau: { label: 'Nouvelle', cls: 'bg-[hsl(var(--brand-mint))] text-[hsl(var(--primary))]' },
  entretien: { label: 'Entretien', cls: 'bg-amber-100 text-amber-700' },
  retenu: { label: 'Retenue', cls: 'bg-[hsl(var(--primary))] text-white' },
  refuse: { label: 'Refusée', cls: 'bg-[hsl(var(--secondary))] text-muted-foreground' },
};
const FILTERS = [['all', 'Toutes'], ['nouveau', 'Nouvelles'], ['entretien', 'Entretien'], ['retenu', 'Retenues'], ['refuse', 'Refusées']];

export function AdminCandidaturesTab({ adminKey, onCount }) {
  const [candidatures, setCandidatures] = useState([]);
  const [filter, setFilter] = useState('all');
  const [error, setError] = useState('');

  const fetchCandidatures = useCallback(async () => {
    setError('');
    try {
      const res = await adminGet('/api/admin/candidatures', adminKey);
      const items = res.data.items || [];
      setCandidatures(items);
      if (onCount) onCount(items.filter((c) => c.status === 'nouveau').length);
    } catch (err) {
      setError(reportError('chargement des candidatures', err) || 'Impossible de charger les candidatures');
      toast.error('Impossible de charger les candidatures');
    }
  }, [adminKey, onCount]);

  useEffect(() => { fetchCandidatures(); }, [fetchCandidatures]);

  const updateStatus = async (id, newStatus) => {
    try {
      await adminPut(`/api/admin/candidatures/${id}/status`, adminKey, { new_status: newStatus });
      setCandidatures((prev) => prev.map((c) => (c._id === id ? { ...c, status: newStatus } : c)));
      toast.success(`Statut mis à jour : ${CAND_STATUS[newStatus]?.label || newStatus}`);
    } catch (err) {
      reportError('statut candidature', err, () => toast.error('Erreur lors de la mise à jour'));
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Supprimer définitivement cette candidature et le CV associé ?')) return;
    try {
      await adminDelete(`/api/admin/candidatures/${id}`, adminKey);
      setCandidatures((prev) => prev.filter((c) => c._id !== id));
      toast.success('Candidature supprimée');
    } catch (err) {
      reportError('suppression candidature', err, () => toast.error('Erreur lors de la suppression'));
    }
  };

  const visible = candidatures.filter((c) => filter === 'all' || c.status === filter);

  return (
    <div className="space-y-3" data-testid="admin-candidatures-list">
      <div className="flex flex-wrap items-center gap-2 mb-2">
        {FILTERS.map(([value, label]) => (
          <Button key={value} size="sm" variant={filter === value ? 'default' : 'outline'} onClick={() => setFilter(value)} data-testid={`cand-filter-${value}`}>{label}</Button>
        ))}
        <Button size="sm" variant="ghost" onClick={fetchCandidatures} aria-label="Actualiser"><RefreshCw className="h-4 w-4" /></Button>
      </div>
      {error && <Card className="p-4 text-sm text-destructive" data-testid="cand-error">{error}</Card>}
      {!error && visible.length === 0 ? (
        <Card className="p-8 text-center"><p className="text-muted-foreground" data-testid="cand-empty">Aucune candidature pour le moment.</p></Card>
      ) : (
        visible.map((c) => {
          const st = CAND_STATUS[c.status] || CAND_STATUS.nouveau;
          return (
            <motion.div key={c._id} initial="hidden" animate="visible" variants={fadeUp}>
              <Card className="p-4 sm:p-5" data-testid="cand-card">
                <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <Badge className={`${st.cls} hover:opacity-90`}>{st.label}</Badge>
                      <Badge variant="secondary" className="text-xs">{c.poste_label}</Badge>
                      <span className="text-xs text-muted-foreground"><Clock className="h-3 w-3 inline mr-1" />{formatDate(c.created_at)}</span>
                    </div>
                    <h3 className="font-semibold text-sm">{c.prenom} {c.nom}{c.ville ? <span className="text-muted-foreground font-normal"> — {c.ville}</span> : null}</h3>
                    <div className="flex gap-3 mt-1 text-xs text-muted-foreground flex-wrap">
                      <a href={`mailto:${c.email}`} className="hover:text-[hsl(var(--primary))]"><Mail className="h-3 w-3 inline mr-1" />{c.email}</a>
                      <a href={`tel:${c.telephone}`} className="hover:text-[hsl(var(--primary))]"><PhoneIcon className="h-3 w-3 inline mr-1" />{c.telephone}</a>
                    </div>
                    <div className="flex gap-2 mt-2 flex-wrap text-xs">
                      {c.experience && <Badge variant="outline">{c.experience}</Badge>}
                      {c.disponibilite && <Badge variant="outline">Dispo : {c.disponibilite}</Badge>}
                      <Badge variant="outline" className="text-[hsl(var(--primary))]">Permis B ✓</Badge>
                      <Badge variant="outline" className="text-[hsl(var(--primary))]">Véhicule ✓</Badge>
                    </div>
                    {c.message && <p className="text-sm text-muted-foreground mt-2">« {c.message} »</p>}
                    {c.cv && <p className="text-xs text-muted-foreground mt-2"><FileText className="h-3 w-3 inline mr-1" />{c.cv.filename} ({Math.round((c.cv.size || 0) / 1024)} Ko)</p>}
                  </div>
                  <div className="flex flex-col gap-2 min-w-[190px]">
                    <a href={`${BACKEND_URL}/api/admin/candidatures/${c._id}/cv?key=${adminKey}`} target="_blank" rel="noopener noreferrer">
                      <Button size="sm" className="w-full" data-testid="cand-download-cv"><Download className="h-3.5 w-3.5 mr-1" /> Télécharger le CV</Button>
                    </a>
                    <Select value={c.status} onValueChange={(v) => updateStatus(c._id, v)}>
                      <SelectTrigger className="h-9" data-testid="cand-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(CAND_STATUS).map(([v, s]) => <SelectItem key={v} value={v}>{s.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => remove(c._id)} data-testid="cand-delete"><Trash2 className="h-3.5 w-3.5 mr-1" /> Supprimer (RGPD)</Button>
                  </div>
                </div>
              </Card>
            </motion.div>
          );
        })
      )}
    </div>
  );
}
